import React, { useState, useEffect, useRef, useCallback } from 'react';

const DEBOUNCE_DELAY = 350; // ms

const UserSearch = ({ searchQuery, onSearch, filters, onFilterChange }) => {
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery || '');
  const debounceRef = useRef(null);
  const isInitialMount = useRef(true);

  // Sync local state when parent searchQuery changes externally
  useEffect(() => {
    setLocalSearchQuery(searchQuery || '');
  }, [searchQuery]);

  // Debounced auto-search — triggers search as user types
  const debouncedSearch = useCallback((value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearch(value);
    }, DEBOUNCE_DELAY);
  }, [onSearch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setLocalSearchQuery(value);
    debouncedSearch(value);
  };

  const handleClearSearch = () => {
    setLocalSearchQuery('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onSearch('');
  };

  // Still allow form submit for explicit search with Enter key
  const handleSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onSearch(localSearchQuery);
  };

  return (
    <div className="search-filters-card">
      <form onSubmit={handleSubmit} className="filters-row">
        {/* Search Input */}
        <div className="search-group">
          <label className="filter-label" htmlFor="user-search">
            <i className="fas fa-search"></i> Search Users
          </label>
          <div className="input-wrapper">
            <i className="fas fa-search input-icon"></i>
            <input
              id="user-search"
              type="text"
              className="form-input"
              placeholder="Search by username, email, or wallet address..."
              value={localSearchQuery}
              onChange={handleInputChange}
              autoComplete="off"
            />
            {localSearchQuery && (
              <button
                type="button"
                className="clear-search-btn"
                onClick={handleClearSearch}
                title="Clear search"
                aria-label="Clear search"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>

        {/* Status Filter */}
        <div className="filter-group">
          <label className="filter-label" htmlFor="status-filter">
            <i className="fas fa-user-check"></i> Status
          </label>
          <select
            id="status-filter"
            className="form-select"
            value={filters.status}
            onChange={(e) => onFilterChange('status', e.target.value)}
          >
            <option value="all">All Users</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>

        {/* User Type Filter */}
        <div className="filter-group">
          <label className="filter-label" htmlFor="type-filter">
            <i className="fas fa-user-tag"></i> User Type
          </label>
          <select
            id="type-filter"
            className="form-select"
            value={filters.userType}
            onChange={(e) => onFilterChange('userType', e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="client">Client</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
          </select>
        </div>

        {/* Submit Button — kept for explicit search & accessibility */}
        <div className="filter-group" style={{ alignSelf: 'flex-end' }}>
          <button type="submit" className="btn-primary" style={{ width: '100%' }}>
            <i className="fas fa-search"></i>
            <span>Search</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserSearch;
